import { useEffect, useRef, useState } from 'react';
import { AuthStatus } from 'constants/auth';
import type { SportsEnum } from 'types/generated/client';
import { useGetCurrentUser } from 'hooks/useGetCurrentUser';
import { useModal } from 'hooks/useModal';
import { useViewer } from 'hooks/useViewer';
import { type ForYouPreferences, useForYouPreferences } from './useForYouPreferences';

export type ForYouScope = 'all' | 'forYou';

interface Options {
  sport: SportsEnum;
  /** Commit the scope: the player's preferences for "For you", null for "All". */
  onApply: (preferences: ForYouPreferences | null) => void;
  /** Gates "For you" behind login for anonymous viewers (`useRequireAuth`'s gate). */
  requireAuth?: (action: () => void) => void;
}

/**
 * The "All / For you" switch behaviour shared by the home feed and /find:
 * "For you" commits the player's onboarding answers as filters (after prompting
 * first-timers to answer the questionnaire), "All" commits the defaults.
 */
export const useForYouScope = ({ sport, onApply, requireAuth }: Options) => {
  const [scope, setScope] = useState<ForYouScope>('all');
  const promptModal = useModal();
  const viewer = useViewer();
  const { user } = useGetCurrentUser();
  const hasCompletedOnboarding = !!user?.playerFinderOnboardedAt;
  // No user row yet. Right after the login modal the viewer context may not have
  // flipped to User either, so "signed in" here means: not confirmed anonymous, or
  // the click came through the auth gate (which only replays after a login).
  const isUserPending = !user && (viewer.status !== AuthStatus.Anonymous || !!requireAuth);
  const { preferences } = useForYouPreferences(sport);
  // Set when the switch flips to For you before the preferences resolved; consumed once.
  const awaitingPreferences = useRef(false);
  // A "For you" flip parked until the user row lands, so first-timers get the
  // onboarding prompt (not a silent empty preset) straight after signing up.
  const pendingScope = useRef<ForYouScope | null>(null);

  const resolveScope = (next: ForYouScope) => {
    if (next === 'forYou' && !hasCompletedOnboarding) {
      promptModal.openModal();
      return;
    }
    setScope(next);
    if (next === 'forYou') {
      if (preferences) onApply(preferences);
      else awaitingPreferences.current = true;
      return;
    }
    onApply(null);
  };

  // The auth gate replays a click captured before login, so it must read the live
  // state through refs: by the time it runs, the user row has often already landed
  // (and the effect below has already come and gone).
  const isUserPendingRef = useRef(isUserPending);
  isUserPendingRef.current = isUserPending;
  const resolveScopeRef = useRef(resolveScope);
  resolveScopeRef.current = resolveScope;

  const applyScope = (next: ForYouScope) => {
    if (next === 'forYou' && isUserPendingRef.current) {
      pendingScope.current = next;
      return;
    }
    resolveScopeRef.current(next);
  };

  useEffect(() => {
    if (pendingScope.current && !isUserPending) {
      const next = pendingScope.current;
      pendingScope.current = null;
      resolveScopeRef.current(next);
    }
  }, [isUserPending]);

  const changeScope = (next: ForYouScope) =>
    next === 'forYou' && requireAuth ? requireAuth(() => applyScope(next)) : applyScope(next);

  // Apply once if the preferences resolved after the switch; never re-apply later
  // (e.g. on sport change), which would clobber the user's committed edits.
  useEffect(() => {
    if (preferences && awaitingPreferences.current) {
      awaitingPreferences.current = false;
      onApply(preferences);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [preferences]);

  return { scope, changeScope, preferences, promptModal };
};
